import { getStripeConfig, StripePrices } from './billingAPI';

/**
 * Single source of truth for plan -> Stripe price resolution.
 *
 * Price ids come from the API (`GET /api/billing/config`) so the Stripe
 * test -> live cutover is a server-side secret swap only. There is deliberately
 * NO hardcoded fallback: if the config can't be loaded, callers get a
 * StripePriceUnavailableError and must surface it instead of sending an
 * empty or stale price id to Stripe.
 */

export type StripePriceKey = keyof StripePrices;

export const STRIPE_PRICE_UNAVAILABLE_MESSAGE =
  'Plan pricing is currently unavailable. Please try again later or contact support.';

export class StripePriceUnavailableError extends Error {
  constructor(message: string = STRIPE_PRICE_UNAVAILABLE_MESSAGE) {
    super(message);
    this.name = 'StripePriceUnavailableError';
  }
}

/**
 * Map subscription tier to its Stripe price key.
 * Tier enum values: Free=0, Launch=5, Basic=10, Plus=20, Premium=30, Enterprise=40.
 *
 * Backend maps its Stripe config "Basic" price to tier name "launch" (marketing
 * rebrand: $29 Basic is displayed as "Launch"). Both Launch (5) and Basic (10)
 * resolve to the same $29 Stripe price — otherwise users on the "launch" tier
 * would appear to have no Stripe subscription, and upgrades would create
 * duplicate subscriptions instead of modifying the existing one.
 */
const tierPriceKeyMap: Record<number, StripePriceKey> = {
  5: 'basic',
  10: 'basic',
  20: 'plus',
  30: 'premium',
  40: 'enterprise',
};

/** Returns the Stripe price key for a tier, or null for tiers with no Stripe price (Free). */
export const getTierPriceKey = (tier: number): StripePriceKey | null =>
  tierPriceKeyMap[tier] ?? null;

/** Whether a tier is billed through a Stripe price (config-independent). */
export const tierHasStripePrice = (tier: number): boolean => getTierPriceKey(tier) !== null;

// Cached in-flight/settled lookup. Only successful lookups stay cached so a
// transient failure can be retried on the next action.
let pricesPromise: Promise<StripePrices> | null = null;

const loadStripePrices = async (): Promise<StripePrices> => {
  let config;

  try {
    config = await getStripeConfig();
  } catch {
    throw new StripePriceUnavailableError();
  }

  if (!config?.prices) {
    throw new StripePriceUnavailableError();
  }

  return config.prices;
};

/** Fetches (once) and returns the Stripe price ids from the billing config. */
export const getStripePrices = (): Promise<StripePrices> => {
  if (!pricesPromise) {
    pricesPromise = loadStripePrices().catch((error) => {
      pricesPromise = null;
      throw error;
    });
  }

  return pricesPromise;
};

/**
 * Resolve the Stripe price id for a tier from the billing config.
 * Throws StripePriceUnavailableError when the tier has no Stripe price, the
 * config call fails, or the config lacks a price for the tier.
 */
export const resolveTierPriceId = async (tier: number): Promise<string> => {
  const key = getTierPriceKey(tier);

  if (!key) {
    throw new StripePriceUnavailableError('The selected plan has no Stripe price.');
  }

  const prices = await getStripePrices();
  const priceId = prices[key];

  if (!priceId) {
    // Don't keep serving a config that lacks this plan's price: drop the cache
    // so a retry refetches, like the other config failures.
    pricesPromise = null;
    throw new StripePriceUnavailableError();
  }

  return priceId;
};

/** Test helper: clear the cached billing config lookup. */
export const resetStripePriceCache = (): void => {
  pricesPromise = null;
};
